import { Form } from 'antd';
import type { CardAudience } from '@crm/shared';
import { CreatableSelect } from '../../components/CreatableSelect';
import { useCreateList, useCreateSegment, useCreateTag, useLists, useSegments, useTags } from '../../features/audience/api';

/** Drops ids of tags, lists or segments that no longer exist (or belong to another project), once those are loaded. */
export function useKnownAudience(a: CardAudience): CardAudience {
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const known = (ids: string[], items: { id: string }[] | undefined) => (items ? ids.filter((id) => items.some((i) => i.id === id)) : ids);
  return { tagIds: known(a.tagIds, tags.data), listIds: known(a.listIds, lists.data), segmentIds: known(a.segmentIds, segments.data) };
}

/** Tags, lists and segments a scanned card's contact is added to; new ones can be created from the search text. */
export function CardAudienceFields({ value, onChange }: { value: CardAudience; onChange: (v: CardAudience) => void }) {
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const createTag = useCreateTag();
  const createList = useCreateList();
  const createSegment = useCreateSegment();
  return (
    <Form layout="vertical" component="div">
      <Form.Item label="Tags" style={{ marginBottom: 12 }}>
        <CreatableSelect
          multiple
          ariaLabel="Tags"
          items={tags.data}
          value={value.tagIds}
          onChange={(tagIds) => onChange({ ...value, tagIds })}
          placeholder="Choose tags or type to create one"
          create={{ noun: 'tag', pending: createTag.isPending, run: async (name) => (await createTag.mutateAsync({ name })).id }}
        />
      </Form.Item>
      <Form.Item label="Lists" style={{ marginBottom: 12 }}>
        <CreatableSelect
          multiple
          ariaLabel="Lists"
          items={lists.data}
          value={value.listIds}
          onChange={(listIds) => onChange({ ...value, listIds })}
          placeholder="Choose lists or type to create one"
          create={{ noun: 'list', pending: createList.isPending, run: async (name) => (await createList.mutateAsync({ name })).id }}
        />
      </Form.Item>
      <Form.Item label="Segments" style={{ marginBottom: 0 }} extra="Contacts added to a segment stay in it whatever its rules say.">
        <CreatableSelect
          multiple
          ariaLabel="Segments"
          items={segments.data}
          value={value.segmentIds}
          onChange={(segmentIds) => onChange({ ...value, segmentIds })}
          placeholder="Choose segments or type to create one"
          create={{ noun: 'segment', pending: createSegment.isPending, run: async (name) => (await createSegment.mutateAsync({ name, rules: { combinator: 'and', conditions: [] } })).id }}
        />
      </Form.Item>
    </Form>
  );
}
